import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getClientsOnce } from '../../services/clientsService';
import { isBrokerPersona } from '../../constants/personas';
import { Trash2 } from 'lucide-react';
import UserCompaniesSection from './UserCompaniesSection';
import useGooglePlacesAutocomplete from '../../hooks/useGooglePlacesAutocomplete';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { parseGooglePlaceAddress } from '../../utils/parseGooglePlaceAddress';
import { formatNorthAmericanPhoneInput } from '../../utils/phoneFormat';
import { isGooglePlacesPacVisible } from '../../utils/googlePlacesPac';

/**
 * @param {{ onClose: () => void, initialTab?: 'personal' | 'companies', overlayClassName?: string }} props
 * - overlayClassName: optional root overlay classes (e.g. higher z-index when stacking over another modal).
 */
export default function ProfileModal({ onClose, initialTab, overlayClassName }) {
  const { t, language, setLanguage } = useLanguage();
  const {
    currentUser,
    userProfile,
    updateUserProfile,
    deleteAccount,
    reauthenticateWithGoogle,
    reauthenticateWithEmailPassword,
  } = useAuth();
  const [activeTab, setActiveTab] = useState(() =>
    initialTab === 'companies' ? 'companies' : 'personal',
  );
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [personalStreet, setPersonalStreet] = useState('');
  const [personalCity, setPersonalCity] = useState('');
  const [personalProvince, setPersonalProvince] = useState('');
  const [personalPostalCode, setPersonalPostalCode] = useState('');
  const [personalCountry, setPersonalCountry] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [profileLanguage, setProfileLanguage] = useState('en');
  const [notifyDirectMessageEmail, setNotifyDirectMessageEmail] = useState(true);
  const [purchaseOffersEnabled, setPurchaseOffersEnabled] = useState(false);
  const [personalSaveSuccess, setPersonalSaveSuccess] = useState(false);
  const personalSaveSuccessTimerRef = useRef(null);

  const isGoogleUser = currentUser?.providerData?.some((p) => p.providerId === 'google.com');
  const isEmailPasswordUser = currentUser?.providerData?.some((p) => p.providerId === 'password');
  const profilePersona = userProfile?.persona || 'investisseur';

  const personalAddressAutocompleteOptions = useMemo(
    () => ({
      componentRestrictions: { country: 'ca' },
      fields: ['address_components'],
    }),
    [],
  );

  const handlePersonalPlaceSelected = useCallback((place) => {
    const parsed = parseGooglePlaceAddress(place);
    setPersonalStreet(parsed.street);
    setPersonalCity(parsed.city);
    setPersonalProvince(parsed.province);
    setPersonalPostalCode(parsed.postalCode);
    if (parsed.country) setPersonalCountry(parsed.country);
    else if (parsed.countryShort) setPersonalCountry(parsed.countryShort);
  }, []);

  const personalStreetInputRef = useGooglePlacesAutocomplete(
    handlePersonalPlaceSelected,
    personalAddressAutocompleteOptions,
  );

  useEffect(() => {
    setDisplayName(
      userProfile?.displayName ?? currentUser?.displayName ?? currentUser?.email ?? '',
    );
    setAvatarUrl(userProfile?.avatarUrl ?? currentUser?.photoURL ?? '');
    if (userProfile?.language === 'fr' || userProfile?.language === 'en') {
      setProfileLanguage(userProfile.language);
    } else if (language === 'fr' || language === 'en') {
      setProfileLanguage(language);
    }
    setPhone(
      formatNorthAmericanPhoneInput(userProfile?.personalPhone ?? userProfile?.phone ?? ''),
    );
    setCompanyName(userProfile?.companyName ?? '');
    setPersonalStreet(userProfile?.personalAddressStreet ?? '');
    setPersonalCity(userProfile?.personalAddressCity ?? '');
    setPersonalProvince(userProfile?.personalAddressProvince ?? '');
    setPersonalPostalCode(userProfile?.personalAddressPostalCode ?? '');
    setPersonalCountry(userProfile?.personalAddressCountry ?? '');
    setProfileBio(userProfile?.profileBio ?? '');
    setNotifyDirectMessageEmail(userProfile?.notifyDirectMessageEmail !== false);
    setPurchaseOffersEnabled(userProfile?.modules?.purchaseOffersEnabled === true);
  }, [currentUser, userProfile, language]);

  useEffect(() => {
    return () => {
      if (personalSaveSuccessTimerRef.current) clearTimeout(personalSaveSuccessTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isGooglePlacesPacVisible()) return;
    setError(null);
    setPersonalSaveSuccess(false);
    setSaving(true);
    try {
      await updateUserProfile({
        displayName: displayName.trim() || undefined,
        avatarUrl: avatarUrl.trim() || undefined,
        language: profileLanguage === 'fr' || profileLanguage === 'en' ? profileLanguage : undefined,
        personalPhone: phone.trim() || null,
        companyName: isBrokerPersona(profilePersona) ? companyName.trim() || null : null,
        personalAddressStreet: personalStreet.trim() || null,
        personalAddressCity: personalCity.trim() || null,
        personalAddressProvince: personalProvince.trim() || null,
        personalAddressPostalCode: personalPostalCode.trim() || null,
        personalAddressCountry: personalCountry.trim() || null,
        profileBio: profileBio.trim() || null,
        notifyDirectMessageEmail: notifyDirectMessageEmail ? true : false,
        modules: {
          ...(userProfile?.modules && typeof userProfile.modules === 'object' ? userProfile.modules : {}),
          ...(profilePersona === 'courtier_immo'
            ? { purchaseOffersEnabled: !!purchaseOffersEnabled }
            : {}),
        },
      });
      if (profileLanguage !== language) setLanguage(profileLanguage);
      setPersonalSaveSuccess(true);
      if (personalSaveSuccessTimerRef.current) clearTimeout(personalSaveSuccessTimerRef.current);
      personalSaveSuccessTimerRef.current = setTimeout(() => {
        setPersonalSaveSuccess(false);
        personalSaveSuccessTimerRef.current = null;
      }, 6000);
    } catch (err) {
      console.error(err);
      setError(err?.message || t('profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    if (isEmailPasswordUser && !deletePassword.trim()) {
      setDeleteError(t('profile.deleteAccountPasswordHint'));
      return;
    }
    setDeleting(true);
    try {
      if (isBrokerPersona(userProfile?.persona) && currentUser?.uid) {
        const clients = await getClientsOnce(currentUser.uid);
        if (clients.length > 0) {
          setDeleteError(t('profile.deleteAccountBrokerHasClients'));
          return;
        }
      }
      if (isGoogleUser) {
        await reauthenticateWithGoogle();
      } else if (isEmailPasswordUser) {
        await reauthenticateWithEmailPassword(deletePassword);
      }
      await deleteAccount();
      onClose();
    } catch (err) {
      console.error(err);
      setDeleteError(err?.message || t('profile.deleteAccountError'));
    } finally {
      setDeleting(false);
    }
  };

  const overlayRootClass =
    overlayClassName ||
    'fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center bg-black/50 p-3 overflow-x-hidden overflow-y-auto';

  const handleEscape = useCallback(() => {
    if (showDeleteConfirm) {
      setShowDeleteConfirm(false);
      setDeleteError(null);
      setDeletePassword('');
    } else {
      onClose();
    }
  }, [showDeleteConfirm, onClose]);

  useRegisterTopEscapeClose(handleEscape);

  return (
    <div className={overlayRootClass}>
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-lg w-full min-w-0 mx-auto my-auto max-h-[min(92vh,720px)] flex flex-col px-3 py-4 sm:px-4 sm:py-5 box-border">
        <div className="flex justify-between items-center shrink-0 mb-3">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('profile.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label={t('cancel')}
          >
            <span className="text-2xl leading-none">&times;</span>
          </button>
        </div>

        <div className="flex shrink-0 border-b border-gray-200 mb-3 gap-1" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'personal'}
            onClick={() => setActiveTab('personal')}
            className={`flex-1 py-2.5 px-3 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              activeTab === 'personal'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {t('profile.tabPersonal')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'companies'}
            onClick={() => setActiveTab('companies')}
            className={`flex-1 py-2.5 px-3 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              activeTab === 'companies'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {t('profile.tabCompanies')}
          </button>
        </div>

        <div className="p-1 flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden w-full">
          {activeTab === 'personal' && (
            <form onSubmit={handleSubmit} className="space-y-4 pb-2">
              {personalSaveSuccess && (
                <div
                  className="rounded-lg border border-emerald-200 bg-emerald-50/90 px-3 py-2.5 text-sm text-emerald-900 italic text-center leading-relaxed"
                  role="status"
                >
                  {t('profile.saveSuccessVerse')}
                </div>
              )}
              {currentUser?.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.emailReadOnly')}</label>
                  <input
                    type="email"
                    value={currentUser.email}
                    readOnly
                    disabled
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-600 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.displayName')}
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder={t('profile.displayNamePlaceholder')}
                />
              </div>
              {isBrokerPersona(profilePersona) ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.companyName')}
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              ) : null}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.phone')}
                </label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatNorthAmericanPhoneInput(e.target.value))}
                  onBlur={() => setPhone((p) => formatNorthAmericanPhoneInput(p))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="(555) 555-5555"
                  autoComplete="tel"
                />
              </div>

              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">{t('profile.personalAddressSection')}</h3>
                <p className="text-xs text-gray-500 mb-3">{t('profile.personalAddressHint')}</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('profile.addressStreet')}</label>
                    <input
                      ref={personalStreetInputRef}
                      type="text"
                      value={personalStreet}
                      onChange={(e) => setPersonalStreet(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      autoComplete="street-address"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('profile.addressCity')}</label>
                      <input
                        type="text"
                        value={personalCity}
                        onChange={(e) => setPersonalCity(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        autoComplete="address-level2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('profile.addressProvince')}</label>
                      <input
                        type="text"
                        value={personalProvince}
                        onChange={(e) => setPersonalProvince(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        autoComplete="address-level1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('profile.addressPostalCode')}</label>
                      <input
                        type="text"
                        value={personalPostalCode}
                        onChange={(e) => setPersonalPostalCode(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        autoComplete="postal-code"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('profile.addressCountry')}</label>
                      <input
                        type="text"
                        value={personalCountry}
                        onChange={(e) => setPersonalCountry(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        autoComplete="country-name"
                        placeholder="Canada"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.avatarUrl')}</label>
                <input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="https://..."
                />
                {avatarUrl && (
                  <div className="mt-2 flex items-center gap-2">
                    <img
                      src={avatarUrl}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.bio')}</label>
                <textarea
                  value={profileBio}
                  onChange={(e) => setProfileBio(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y min-h-[72px]"
                  placeholder={t('profile.bioPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.persona')}
                </label>
                <p
                  data-onboarding="profile-persona"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-800 text-sm"
                >
                  {t(`persona.${profilePersona}`)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('profile.language')}
                </label>
                <select
                  data-onboarding="profile-language"
                  value={profileLanguage}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'fr' || val === 'en') {
                      setProfileLanguage(val);
                      setLanguage(val);
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyDirectMessageEmail}
                  onChange={(e) => setNotifyDirectMessageEmail(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">{t('profile.notifyDirectMessageEmail')}</span>
              </label>

              {profilePersona === 'courtier_immo' ? (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={purchaseOffersEnabled}
                    onChange={(e) => setPurchaseOffersEnabled(e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>
                    <span className="text-sm font-medium text-gray-800 block">{t('profile.purchaseOffersModule')}</span>
                    <span className="text-xs text-gray-600 block mt-0.5">{t('profile.purchaseOffersModuleHint')}</span>
                  </span>
                </label>
              ) : null}

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
              <div className="flex gap-2 sticky bottom-0 bg-white pt-2 pb-1 border-t border-gray-100 -mx-1 px-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 hover:bg-blue-700"
                >
                  {saving ? t('loading') : t('profile.save')}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'companies' && (
            <div className="pb-2">
              <UserCompaniesSection embedded />
            </div>
          )}
        </div>

        <div className="shrink-0 mt-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => { setDeleteError(null); setShowDeleteConfirm(true); }}
            className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 hover:underline"
          >
            <Trash2 className="w-4 h-4" />
            {t('profile.deleteAccount')}
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[230] rentalyzer-modal-overlay rentalyzer-modal-overlay-nested flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <p className="text-gray-700 mb-4">{t('profile.deleteAccountWarning')}</p>
            {isGoogleUser && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                {t('profile.deleteAccountReauthGoogle')}
              </p>
            )}
            {isEmailPasswordUser && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.password')}
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder={t('profile.deleteAccountPasswordHint')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  autoComplete="current-password"
                />
              </div>
            )}
            {deleteError && (
              <p className="text-sm text-red-600 mb-3">{deleteError}</p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); setDeletePassword(''); }}
                disabled={deleting}
                className="flex-1 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting || (isEmailPasswordUser && !deletePassword.trim())}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 hover:bg-red-700"
              >
                {deleting ? t('profile.deleting') : t('profile.deleteAccountConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
